import React, { useMemo } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import type { ChartType } from './types'
import { DATA_TYPE_OPTIONS, CHART_TYPE_LABELS } from './types'
import { TableViewSettingsData } from '../../types/app'
import { getSelectedPositions, getSelectedColumnLabel } from './utils/selectionHelpers'
import { chartableAxis, toDisplayPositions, fromDisplayPositions } from '../../utils/tableSelection'
import { SelectDataDropdown } from '../dashboard/widgets/SelectDataRow'

interface GraphConfigControlsProps {
  allRows: string[];
  allColumns: string[];
  allRowTypes: string[];
  allColumnTypes: string[];
  allowedChartTypes: ChartType[];
  viewSettings: TableViewSettingsData;
  onViewSettingsChange: (settings: TableViewSettingsData) => void;
}

/**
 * GraphConfigControls Component
 *
 * Provides chart configuration controls (chart type, data type, row/column filtering)
 * Extracted from GraphControls to be reusable in different layouts
 */
export const GraphConfigControls: React.FC<GraphConfigControlsProps> = ({
  allRows,
  allColumns,
  allRowTypes,
  allColumnTypes,
  allowedChartTypes,
  viewSettings,
  onViewSettingsChange,
}) => {
  // Charts never draw base rows/columns (Cases WF, totals) — offering them in
  // these dropdowns gives options that change nothing on screen (#1173).
  // `chartableAxis` returns the chartable labels AND the TRUE vec positions
  // they came from; the dropdown addresses OPTION indices while
  // `selected_rows`/`selected_columns` store TRUE positions (as index
  // strings), so the two are mapped across this boundary via
  // toDisplayPositions/fromDisplayPositions — the same contract the
  // dashboard chart widget's TableSelectionEditor uses. Position, not label,
  // is what makes two rows sharing a label independently selectable
  // (#1173 cause A) — labels still repeat once per brand group.
  const rowAxis = useMemo(
    () => chartableAxis(allRows, allRowTypes),
    [allRows, allRowTypes],
  )
  const columnAxis = useMemo(
    () => chartableAxis(allColumns, allColumnTypes),
    [allColumns, allColumnTypes],
  )

  const selectedRowPositions = useMemo(
    () => getSelectedPositions(viewSettings.selected_rows, allRows),
    [viewSettings.selected_rows, allRows],
  )
  const selectedColumnPositions = useMemo(
    () => getSelectedPositions(viewSettings.selected_columns, allColumns),
    [viewSettings.selected_columns, allColumns],
  )

  // Base positions an existing widget already stored (from before this fix,
  // when bases were selectable here) — carried through untouched rather than
  // silently dropped the next time the user edits this axis.
  const storedBaseRows = useMemo(
    () => selectedRowPositions.filter(p => !rowAxis.positions.includes(p)),
    [selectedRowPositions, rowAxis.positions],
  )
  const storedBaseColumns = useMemo(
    () => selectedColumnPositions.filter(p => !columnAxis.positions.includes(p)),
    [selectedColumnPositions, columnAxis.positions],
  )

  const rowDisplayPositions = useMemo(
    () => toDisplayPositions(selectedRowPositions, rowAxis.positions),
    [selectedRowPositions, rowAxis.positions],
  )
  const columnDisplayPositions = useMemo(
    () => toDisplayPositions(selectedColumnPositions, columnAxis.positions),
    [selectedColumnPositions, columnAxis.positions],
  )

  const selectedColumn = useMemo(() => {
    return getSelectedColumnLabel(viewSettings.selected_column, allColumns) || allColumns[0]
  }, [viewSettings.selected_column, allColumns])

  // // Auto-populate selections with non-Base fields when empty
  // useEffect(() => {
  //   let needsUpdate = false;
  //   const updates: Partial<TableViewSettingsData> = {};

  //   // If selected_rows is empty and we have type information, populate with non-Base indices
  //   if (viewSettings.selected_rows.length === 0 && allRowTypes.length > 0 && allRows.length > 0) {
  //     const nonBaseRowIndices = allRows
  //       .map((_, index) => {
  //         const rowType = allRowTypes[index] || '';
  //         return rowType.toLowerCase() !== 'base' ? index.toString() : null;
  //       })
  //       .filter((idx): idx is string => idx !== null);

  //     if (nonBaseRowIndices.length > 0) {
  //       updates.selected_rows = nonBaseRowIndices;
  //       needsUpdate = true;
  //     }
  //   }

  //   // If selected_columns is empty and we have type information, populate with non-Base indices
  //   if (viewSettings.selected_columns.length === 0 && allColumnTypes.length > 0 && allColumns.length > 0) {
  //     const nonBaseColumnIndices = allColumns
  //       .map((_, index) => {
  //         const colType = allColumnTypes[index] || '';
  //         return colType.toLowerCase() !== 'base' ? index.toString() : null;
  //       })
  //       .filter((idx): idx is string => idx !== null);

  //     if (nonBaseColumnIndices.length > 0) {
  //       updates.selected_columns = nonBaseColumnIndices;
  //       needsUpdate = true;
  //     }
  //   }

  //   if (needsUpdate) {
  //     onViewSettingsChange({ ...viewSettings, ...updates });
  //   }
  // }, [allRows, allColumns, allRowTypes, allColumnTypes, viewSettings, onViewSettingsChange]);

  const chartType = viewSettings.chart_type as ChartType
  const dataType = viewSettings.data_type
  const switchAxis = viewSettings.switch_axis

  // Determine which controls to show based on chart type
  const showSingleColumnSelector = chartType === 'bar' || chartType === 'column' || chartType === 'pie'
  const showMultiSelectFilters =
    chartType !== 'table' && chartType !== 'bar' && chartType !== 'column' && chartType !== 'pie'

  // Handle chart type change
  const handleChartTypeChange = (event: SelectChangeEvent) => {
    onViewSettingsChange({ ...viewSettings, chart_type: event.target.value })
  }

  // Handle data type change
  const handleDataTypeChange = (event: SelectChangeEvent) => {
    onViewSettingsChange({ ...viewSettings, data_type: event.target.value })
  }

  // Handle single column change (for bar/column/pie)
  const handleSingleColumnChange = (event: SelectChangeEvent<string>) => {
    const columnName = event.target.value
    const columnIndex = allColumns.findIndex(col => col === columnName).toString()
    onViewSettingsChange({ ...viewSettings, selected_column: columnIndex })
  }

  // Rows/columns are stored as TRUE vec positions (index strings) so a
  // selection covering every chartable option — with nothing preserved from
  // a legacy base pick — canonicalizes back to [] ("all"), matching what an
  // untouched widget already means (getSelectedRowLabels/getSelectedColumnLabels
  // default to every non-base row when selected_rows is empty).
  const handleRowPositionsChange = (display: number[]) => {
    const truePositions = fromDisplayPositions(display, rowAxis.positions, storedBaseRows)
    const isDefaultAll = storedBaseRows.length === 0 && truePositions.length === rowAxis.positions.length
    onViewSettingsChange({
      ...viewSettings,
      selected_rows: isDefaultAll ? [] : truePositions.map(String),
    })
  }

  const handleColumnPositionsChange = (display: number[]) => {
    const truePositions = fromDisplayPositions(display, columnAxis.positions, storedBaseColumns)
    const isDefaultAll = storedBaseColumns.length === 0 && truePositions.length === columnAxis.positions.length
    onViewSettingsChange({
      ...viewSettings,
      selected_columns: isDefaultAll ? [] : truePositions.map(String),
    })
  }

  // Handle switch axis change
  const handleSwitchAxisChange = (checked: boolean) => {
    onViewSettingsChange({ ...viewSettings, switch_axis: checked })
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {/* Chart Type Select */}
      {allowedChartTypes.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 90 }} variant="outlined">
          <InputLabel id="chart-type-select-label" sx={{ fontSize: '0.7rem' }}>Chart Type</InputLabel>
          <Select
            labelId="chart-type-select-label"
            id="chart-type-select"
            value={chartType}
            label="Chart Type"
            onChange={handleChartTypeChange}
            sx={{ height: '28px', fontSize: '0.7rem' }}
          >
            {allowedChartTypes
              .filter((k) => k !== 'scatter' && k !== 'table')
              .map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {CHART_TYPE_LABELS[kind]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}

      {/* Data Type Select */}
      <FormControl size="small" sx={{ minWidth: 80 }} variant="outlined">
        <InputLabel id="data-type-select-label" sx={{ fontSize: '0.7rem' }}>Data Type</InputLabel>
        <Select
          labelId="data-type-select-label"
          id="data-type-select"
          value={dataType}
          label="Data Type"
          onChange={handleDataTypeChange}
          sx={{ height: '28px', fontSize: '0.7rem' }}
        >
          {DATA_TYPE_OPTIONS.map((dt) => (
            <MenuItem key={dt.value} value={dt.value}>
              {dt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Single Column Selector (for bar/column/pie charts) */}
      {showSingleColumnSelector && allColumns.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 90 }} variant="outlined">
          <InputLabel id="single-column-select-label" sx={{ fontSize: '0.7rem' }}>Column</InputLabel>
          <Select
            labelId="single-column-select-label"
            id="single-column-select"
            value={selectedColumn || ''}
            label="Column"
            onChange={handleSingleColumnChange}
            sx={{ height: '28px', fontSize: '0.7rem' }}
          >
            {allColumns.map((col) => (
              <MenuItem key={col} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Multi-Select Row Filter — chartable rows only (bases excluded, #1173) */}
      {showMultiSelectFilters && rowAxis.options.length > 0 && (
        <SelectDataDropdown
          noun="Row"
          options={rowAxis.options}
          selected={rowDisplayPositions}
          onChange={handleRowPositionsChange}
          showSelectAll
        />
      )}

      {/* Multi-Select Column Filter — chartable columns only (bases excluded, #1173) */}
      {showMultiSelectFilters && columnAxis.options.length > 0 && (
        <SelectDataDropdown
          noun="Column"
          options={columnAxis.options}
          selected={columnDisplayPositions}
          onChange={handleColumnPositionsChange}
          showSelectAll
        />
      )}

      {/* Switch Axis Checkbox - only show for multi-select chart types */}
      {showMultiSelectFilters && (
        <FormControlLabel
          control={
            <Checkbox
              checked={switchAxis}
              onChange={(e) => handleSwitchAxisChange(e.target.checked)}
              size="small"
            />
          }
          label="Switch Axis"
          sx={{ fontSize: '0.7rem', mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
        />
      )}
    </Box>
  )
}

export default GraphConfigControls
